import { dateSuisse } from './dates';
import { tr } from './i18n';
import { periodesComptable } from './cloture';
import { piecesDesDepenses } from './pieces-depenses';

/** « 05.03.2026 » se trie comme « 20260305 ». */
const cleDate = (d) =>
  String(d || '')
    .split('.')
    .reverse()
    .join('');

/**
 * Les types du classeur qui ne sont pas des catégories de dépense : les
 * honoraires du comptable, réglés dans Compta → Comptable.
 */
export const TYPES_DU_CLASSEUR = {
  comptable: { lib: 'Comptable / fiduciaire', compte: '6530', icon: '🧮' },
};

/** Le premier mois (0 à 11) de chaque période de facturation du comptable. */
const DEBUT_DE_PERIODE = { T1: 0, T2: 3, T3: 6, T4: 9, S1: 0, S2: 6, AN: 0 };
/** Le dernier mois (0 à 11) de chaque période. */
const FIN_DE_PERIODE = { T1: 2, T2: 5, T3: 8, T4: 11, S1: 5, S2: 11, AN: 11 };

/**
 * Les honoraires du comptable de l'année, une pièce par période payée —
 * chaque mois, trimestre, semestre ou l'année, selon sa fréquence — jusqu'à
 * la période en cours. Ils comptaient dans les charges sans laisser de trace
 * dans le classeur.
 *
 * @param {object} [comptable] fiche du comptable ({nom, montant, freq, paye})
 * @param {number} annee exercice
 * @param {Date} [maintenant] aujourd'hui
 * @returns {object[]} les pièces ({date, fournisseur, libelle, montant,
 *   periode, paye, origine: 'comptable'})
 */
export function piecesDuComptable(comptable, annee, maintenant = new Date()) {
  const montant = +comptable?.montant || 0;
  if (!(montant > 0) || !annee) return [];
  const regle = comptable.paye || {};
  return periodesComptable(comptable.freq)
    .filter(([cle]) => {
      const debut = DEBUT_DE_PERIODE[cle] ?? +cle - 1;
      return new Date(annee, debut, 1) <= maintenant;
    })
    .map(([cle, lib]) => {
      const fin = FIN_DE_PERIODE[cle] ?? +cle - 1;
      const dernier = new Date(annee, fin + 1, 0);
      return {
        date:
          String(dernier.getDate()).padStart(2, '0') +
          '.' +
          String(fin + 1).padStart(2, '0') +
          '.' +
          annee,
        fournisseur: comptable.nom || tr('Comptable'),
        libelle: tr(lib) + ' ' + annee,
        montant,
        periode: cle,
        paye: !!regle[annee + '-' + cle],
        origine: 'comptable',
      };
    });
}

/**
 * Les dépenses du classeur, rangées par type : celles de la page Dépenses,
 * et aussi les factures de fournisseurs et de sous-traitants jointes aux
 * offres et factures ou saisies sur les chantiers.
 *
 * <p>Le patron scannait la facture de son sous-traitant sur la facture du
 * client : comptée dans ses dépenses et sa TVA, elle n'apparaissait pas dans
 * « Dépenses par type », et il croyait devoir la ressaisir dans Dépenses —
 * où elle aurait compté deux fois. Une pièce déjà saisie dans Dépenses (même
 * montant, même jour) n'est montrée qu'une fois, comme dans les dépenses.
 *
 * @param {object} p pièces de la société
 * @param {object[]} [p.frais] dépenses de la page Dépenses
 * @param {object[]} [p.chantiers] chantiers, leurs factures fournisseurs et sous-traitants
 * @param {object[]} [p.achatsJoints] pièces chiffrées des offres et factures ({genre, documentId, piece})
 * @param {object[]} [p.factures] factures clients, pour nommer le document d'une pièce
 * @param {object[]} [p.devis] offres, pour nommer le document d'une pièce
 * @param {object} [p.comptable] fiche du comptable : ses honoraires, période par période
 * @param {number} [p.annee] exercice des honoraires du comptable
 * @param {Date} [p.maintenant] aujourd'hui
 * @returns {Array<[string, object[]]>} [type, pièces] du plus coûteux au moins coûteux ;
 *   chaque pièce : {date, fournisseur, montant, piece?, pieceJointe?, ou?, payeLe?, payePar?}
 */
export function depensesDuClasseur({
  frais,
  chantiers,
  achatsJoints,
  factures,
  devis,
  comptable,
  annee,
  maintenant,
}) {
  const parType = new Map();
  const ajoute = (type, item) => {
    if (!parType.has(type)) parType.set(type, []);
    parType.get(type).push(item);
  };
  // Les mêmes pièces, chacune une fois, que la comptabilité, l'accueil et le
  // chantier (piecesDesDepenses) : une facture jointe à l'offre puis à la
  // facture, saisie au chantier et dans Dépenses, ne compte qu'une fois.
  const nomDuDocument = (x) => {
    const facture = x?.genre === 'FACTURE';
    const doc = ((facture ? factures : devis) || []).find((d) => d.id === x?.documentId);
    return doc ? tr(facture ? 'Facture' : 'Offre') + ' ' + doc.num : '';
  };
  const ouEst = (g) =>
    g.source === 'joint'
      ? nomDuDocument(g.joint)
      : g.source === 'frais'
        ? tr('Dépenses')
        : g.chantier?.nom || '';
  piecesDesDepenses({ frais, chantiers, achatsJoints, factures, devis }).forEach((g) => {
    const aussi = g.doublons.map(ouEst).filter(Boolean);
    const fichierDouble = g.doublons.find((d) => d.item?.pieceId || d.item?.fichier)?.item;
    const pieceDuDouble = fichierDouble
      ? fichierDouble.fichier
        ? fichierDouble
        : { id: fichierDouble.pieceId, libelle: fichierDouble.fournisseur }
      : null;
    if (g.source === 'frais') {
      ajoute(g.type, { ...g.item, origine: 'frais', ...(aussi.length ? { aussi } : {}) });
      return;
    }
    const x = g.item;
    if (g.source === 'chantier') {
      ajoute(g.type, {
        date: x.date,
        fournisseur: x.fournisseur,
        montant: +x.montant || 0,
        montantTva: x.montantTva ?? null,
        tauxTva: x.tauxTva ?? null,
        genre: 'fournisseur',
        ou: g.chantier.nom,
        pieceJointe: x.pieceId ? { id: x.pieceId, libelle: x.fournisseur } : pieceDuDouble,
        origine: 'chantier',
        payeLe: x.payeLe || '',
        payePar: x.payePar || '',
        ...(aussi.length ? { aussi } : {}),
      });
      return;
    }
    if (g.source === 'soustraitant') {
      ajoute('soustraitance', {
        date: g.date,
        fournisseur: x.entreprise,
        libelle: g.dejaFacture > 0 ? tr('Reste du prix convenu') : '',
        sansFacture: !!g.sansFacture,
        montant: g.montant,
        montantTva: g.montantTva,
        genre: 'fournisseur',
        ou: g.chantier.nom,
        pieceJointe: x.pieceId ? { id: x.pieceId, libelle: x.entreprise } : null,
        origine: 'chantier',
        payeLe: x.payeLe || '',
        payePar: x.payePar || '',
      });
      return;
    }
    ajoute(g.type, {
      date: g.date,
      fournisseur: x.fournisseur || x.libelle,
      montant: g.montant,
      montantTva: x.montantTva ?? null,
      genre: 'fournisseur',
      ou: nomDuDocument(g.joint),
      pieceJointe: x.fichier ? x : pieceDuDouble,
      origine: 'joint',
      payeLe: x.payeLe ? dateSuisse(x.payeLe) : '',
      payePar: String(x.payePar || '').toLowerCase(),
      ...(aussi.length ? { aussi } : {}),
    });
  });

  piecesDuComptable(comptable, annee, maintenant).forEach((x) => ajoute('comptable', x));

  const total = (arr) => arr.reduce((s, x) => s + (+x.montant || 0), 0);
  return [...parType.entries()]
    .map(([type, arr]) => [
      type,
      arr.sort((a, b) => cleDate(b.date).localeCompare(cleDate(a.date))),
    ])
    .sort((a, b) => total(b[1]) - total(a[1]));
}
